import React from "react";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { em } from "polished";
import uid from "utils/uid";
import tulkojumi from "utils/tulkojumi";
import { Flex, FlexItem, media } from "core/layout";
import {
  colors,
  duration,
  ease,
  themed,
  elevation,
  isDefaultThemeMode,
  accessibilityHoverInvert,
} from "core/theme";
import {
  ExpandLess as ExpandLessIcon,
  ArrowRight as ArrowRightIcon,
  External as ExternalIcon,
} from "core/icons";
import { Section } from "./CardsSection/styled";
import { Heading } from "elements";

const applyBoxShadow = (props) => {
  if (isDefaultThemeMode(props)) {
    return css`
      box-shadow: ${elevation.Z2};
      transition: box-shadow ${duration.fast} ${ease.standard};
      will-change: box-shadow;

      &:hover {
        box-shadow: ${elevation.Z8};
      }
    `;
  } else {
    return css`
      border: 2px solid ${colors.text};
    `;
  }
};

const color = (props) => {
  const defaultThemed = Object.assign({}, colors.lightThemed, {
    light: colors.W,
  });
  return themed(defaultThemed);
};

const Wrapper = styled.div`
  margin: ${em(40)} 0 0;

  /* Virsraksts "Jaunākās ziņas" */
  & + section > div:first-child {
    margin-top: ${em(60)};
  }
`;

const StyledCard = styled.div`
  background-color: ${colors.background};
  @media screen {
    ${applyBoxShadow};
  }
`;

const TitleGroup = styled.div`
  background-color: ${colors.primaryLight};
  padding: ${em(24, 24)};
  text-align: center;

  display: flex;
  flex-direction: row;
  align-items: center;

  > h3 {
    color: ${color};
    font-size: ${em(24)};
    margin-bottom: 0;
    margin-right: ${em(-28)};
    width: 100%;
  }

  ${media.tabletSmall`
    padding: ${em(18, 20)};

    > h3 {
      font-size: ${em(20)};
    }
  `};
`;

const ExpandLabel = styled.span`
  color: ${color};
  display: inline-block;
  font-size: ${em(15)};

  i {
    margin-left: ${em(6)};
    transform: scaleY(-1);
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      i {
        transform: scaleY(1);
      }
    `};
`;

const List = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding-left: 0;

  > li {
    &:first-child > a {
      padding-top: ${em(18)};
    }
    &:last-child > a {
      padding-bottom: ${em(18)};
    }

    ${media.tablet`
      &:first-child > a {
        padding-top: ${em(14)};
      }
      &:last-child > a {
        padding-top: ${em(14)};
      }
    `};
  }
`;

const linkBase = css`
  color: ${colors.text};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1em;
  font-weight: 500;
  text-decoration: none;

  padding: ${em(14)} ${em(20)};

  ${media.tablet`
    padding: ${em(10)} ${em(16)};
  `};

  i {
    color: ${colors.hairline};
    margin-left: ${em(8)};
  }

  &:hover {
    background-color: ${colors.highlight};
    color: ${colors.primary};

    ${accessibilityHoverInvert};

    i {
      color: currentColor;
    }
  }
`;

const StyledExternalLink = styled.a`
  ${linkBase};

  > span > i {
    color: currentColor;
  }
`;

const StyledLink = styled(Link)`
  ${linkBase};
`;

function Card({
  id,
  title,
  items = [],
  isOpen = true,
  onToggleOpen = () => {},
  layout,
  teksti,
}) {
  // const itemsSorted = React.useMemo(
  //   () => [...items].sort((a, b) => a.menu_order - b.menu_order),
  //   [items]
  // );
  const itemsSorted = [...items].sort((a, b) => a.menu_order - b.menu_order);
  const isMobileLayout = layout === "mobile" || layout === "tabletSmall";
  const toggleOpenHandler =
    isMobileLayout && !isOpen
      ? {
          onClick: () => {
            onToggleOpen(!isOpen);
          },
        }
      : {};
  return (
    <StyledCard>
      {title && (
        <TitleGroup {...toggleOpenHandler}>
          <Heading level={3}>{title}</Heading>
          {isMobileLayout && !isOpen ? (
            <ExpandLabel isOpen={isOpen}>
              {tulkojumi(teksti, "atvert")}
              <ExpandLessIcon />
            </ExpandLabel>
          ) : null}
        </TitleGroup>
      )}
      {isOpen || !isMobileLayout ? (
        <List>
          {itemsSorted.map((item) => {
            const { path, title, target } = item;
            const external = path && path.startsWith("http");
            return (
              <li key={uid()}>
                {external ? (
                  <StyledExternalLink
                    href={path}
                    rel="noopener noreferrer"
                    target={target}
                  >
                    <span>
                      {title}
                    </span>
                    <ExternalIcon size="small" />
                    {/* <ArrowRightIcon size="small" /> */}
                  </StyledExternalLink>
                ) : (
                  <StyledLink to={path}>
                    {title}
                    <ArrowRightIcon size="small" />
                  </StyledLink>
                )}
              </li>
            );
          })}
        </List>
      ) : null}
    </StyledCard>
  );
}

class SaitesSection extends React.Component {
  constructor(props) {
    super(props);
    this.groups = Object.keys(props.data).filter(key => key !== "__typename");
    this.state = {
      open: props.layout === "desktop" ? this.groups.map((_, i) => i) : [0],
    };
  }

  render() {
    const { id, data, teksti, layout } = this.props;
    return (
      <Wrapper>
        <Section>
          <Flex>
            {this.groups.map((key, index) => {
              return (
                <FlexItem key={uid()}>
                  <Card
                    id={key}
                    title={tulkojumi(teksti, key)}
                    items={data[key]}
                    isOpen={this.state.open.includes(index)}
                    onToggleOpen={(open) => {
                      this.setState((prevState) => ({
                        ...prevState,
                        open: open
                          ? [...prevState.open, index]
                          : prevState.open.filter((i) => i !== index),
                      }));
                    }}
                    layout={layout}
                    teksti={teksti}
                  />
                </FlexItem>
              );
            })}
          </Flex>
        </Section>
      </Wrapper>
    );
  }
}

export default SaitesSection;



// WEBPACK FOOTER //
// ./src/modules/SaitesSection.jsx